/**
 * Conversation commands — the WRITE half of a project chat (MYAC-1519, option B).
 *
 * In a project chat the browser no longer runs the turn itself (`POST /run_sse`):
 * it writes a COMMAND DOC under
 * `projects/{pid}/command_queues/conversation/commands/{command_id}` and follows
 * the conversation in the `views/chat_sessions` rows. Closing the tab changes
 * nothing; every tab and device sees the same answer.
 *
 * The backend flips the doc's status:
 *   requested → accepted | rejected(reason_key) → running → done | failed(error_key) | cancelled
 * (`queued` is reserved). The doc is member-readable, so `watchCommand` follows it.
 *
 * Ids are minted HERE, by the browser: one per message, so a deposit retried with
 * the same id is the same command (idempotent). The chat id of a new conversation
 * is minted the same way and travels in the first command's payload — the backend
 * creates the ADK session under it.
 *
 * The browser writes the command itself; the rules' create guard is the gate (the
 * `conversation` queue is live on dev since 2026-10-09). No HTTP call.
 */
import { collection, doc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore'
import type { Firestore, Unsubscribe } from 'firebase/firestore'
import { useFirestore } from 'vuefire'
import { getAuth } from 'firebase/auth'
import { logger } from '@/utils/logger'

export const CONVERSATION_QUEUE_ID = 'conversation'

export type ConversationCommandType =
  | 'send_message' | 'decide_action' | 'cancel' | 'rename'
  // MYAC-1269 — sharing: applied at admission on the chat doc and its row, no turn
  | 'share' | 'set_right' | 'set_visibility' | 'remove' | 'leave' | 'stop'

export type ConversationCommandStatus =
  | 'requested'
  | 'queued'
  | 'accepted'
  | 'rejected'
  | 'running'
  | 'done'
  | 'failed'
  | 'cancelled'

/** The `reason_key` values of a rejected command the app has copy for. */
export const CONVERSATION_REASON_KEYS = [
  'invalid_payload',
  'unknown_type',
  'forbidden',
  'chat_not_found',
  'busy',
  'credits',
  'dispatch',
  'internal',
  // MYAC-1269 — a uid named by a share is not a member; a reader may not write
  'not_a_member',
  'read_only',
] as const
export type ConversationReasonKey = (typeof CONVERSATION_REASON_KEYS)[number]

/** The `error_key` values of a failed / cancelled command the app has copy for. */
export const CONVERSATION_ERROR_KEYS = ['interrupted', 'agent_error', 'cancelled'] as const
export type ConversationErrorKey = (typeof CONVERSATION_ERROR_KEYS)[number]

/** Statuses after which nothing more happens on the doc. */
export const TERMINAL_COMMAND_STATUSES: ReadonlySet<ConversationCommandStatus> = new Set([
  'rejected',
  'done',
  'failed',
  'cancelled',
])

export interface SendMessagePayload {
  chat_id: string
  app_name: string
  /** The WIRE message, markers included (`__FOCUS__`, `__ATTACH__`, …). */
  text: string
}

export interface DecideActionPayload {
  chat_id: string
  app_name: string
  proposal_id: string
  decision: 'approve' | 'decline'
  language: 'french' | 'english'
  /** The register-proposal card (`apply_register_proposal`): which action and register. */
  action?: string
  register?: string
}

export interface CancelPayload {
  target_command_id: string
}

/** MYAC-918 — rename a conversation; applied at admission (owner only), no turn. */
export interface RenamePayload {
  chat_id: string
  title: string
}

/** MYAC-1269 — a participant's right on a shared conversation. */
export type ShareRight = 'read' | 'write'
/** MYAC-1269 — who may read a conversation besides its owner. */
export type ShareVisibility = 'private' | 'participants' | 'project'

/** Name participants with one right (owner only); a uid already named takes the new right. */
export interface SharePayload {
  chat_id: string
  uids: string[]
  right: ShareRight
}
/** Change one named participant's right (owner only). */
export interface SetRightPayload {
  chat_id: string
  uid: string
  right: ShareRight
}
/** Share with the whole project (read-only or edit), or stop doing so (owner only). */
export interface SetVisibilityPayload {
  chat_id: string
  visibility: ShareVisibility
  project_right?: ShareRight
}
/** Un-name participants (owner only). */
export interface RemovePayload {
  chat_id: string
  uids: string[]
}
/** The requester, a named participant, removes themself. */
export interface LeavePayload {
  chat_id: string
}
/** Stop every share at once (owner only): the others lose it, history included. */
export interface StopSharingPayload {
  chat_id: string
}

/** One observed state of a command doc. */
export interface ConversationCommandState {
  commandId: string
  status: ConversationCommandStatus
  reasonKey?: string
  errorKey?: string
}

export interface DepositOptions {
  /** Reuse an id already minted for this message (a retried deposit). */
  commandId?: string
  firestore?: Firestore
}

export const conversationCommandsPath = (projectId: string) =>
  `projects/${projectId}/command_queues/${CONVERSATION_QUEUE_ID}/commands`

/** A fresh command id — Firestore's own auto-id, minted client-side. */
export function mintCommandId(projectId: string, firestore?: Firestore): string {
  const db = firestore ?? useFirestore()
  return doc(collection(db, conversationCommandsPath(projectId))).id
}

/** A fresh chat id for a new conversation (the backend creates its session under it). */
export function mintChatId(projectId: string, firestore?: Firestore): string {
  const db = firestore ?? useFirestore()
  return doc(collection(db, `projects/${projectId}/views/chat_sessions/rows`)).id
}

/** Bound an arbitrary `reason_key` to the copy the app has; `internal` otherwise. */
export function conversationReasonKey(key: string | undefined | null): ConversationReasonKey {
  return (CONVERSATION_REASON_KEYS as readonly string[]).includes(key ?? '')
    ? (key as ConversationReasonKey)
    : 'internal'
}

/** Bound an arbitrary `error_key` to the copy the app has; `agent_error` otherwise. */
export function conversationErrorKey(key: string | undefined | null): ConversationErrorKey {
  return (CONVERSATION_ERROR_KEYS as readonly string[]).includes(key ?? '')
    ? (key as ConversationErrorKey)
    : 'agent_error'
}

/** The full i18n key for a (possibly unknown) `reason_key` — never the raw value. */
export const conversationReasonI18nKey = (key: string | undefined | null) =>
  `chat.command.reason.${conversationReasonKey(key)}`

/** The full i18n key for a (possibly unknown) `error_key` — never the raw value. */
export const conversationErrorI18nKey = (key: string | undefined | null) =>
  `chat.command.error.${conversationErrorKey(key)}`

/** Write the command and return its id — a plain Firestore create. */
async function depositCommand(
  projectId: string,
  type: ConversationCommandType,
  payload: SendMessagePayload | DecideActionPayload | CancelPayload | RenamePayload
    | SharePayload | SetRightPayload | SetVisibilityPayload | RemovePayload | LeavePayload
    | StopSharingPayload,
  options: DepositOptions,
): Promise<string> {
  const db = options.firestore ?? useFirestore()
  const user = getAuth().currentUser
  if (!user) throw new Error('not signed in')
  const commandId = options.commandId ?? mintCommandId(projectId, db)
  await setDoc(doc(db, `${conversationCommandsPath(projectId)}/${commandId}`), {
    type,
    payload,
    status: 'requested',
    requested_by: user.uid,
    requested_at: serverTimestamp(),
    command_version: 1,
  })
  localRailAdmit(projectId, commandId)
  return commandId
}

/**
 * DEV ONLY — the local rail (core_agents_tools `conversation_turns`: "with no
 * Eventarc trigger locally, fire the admission by hand"). Under the vite dev
 * server with `VITE_LOCAL_CONVERSATION_RAIL=1`, the command just written is
 * handed at once to the local chat server through the same-origin
 * `/local-chatbot` proxy (`vite.config.ts`: it forwards to the loopback port
 * `MAC_LOCAL_CHATBOT_PORT` and adds the server's key from
 * `MAC_LOCAL_CHATBOT_API_KEY`), so a local admission sees the command before
 * the deployed one does. Fire and forget: the command doc is the truth either
 * way, and a server that is not there changes nothing. Dead code in every
 * built bundle (`DEV` is false). Exported for its test.
 */
export function localRailAdmit(projectId: string, commandId: string): void {
  if (!import.meta.env.DEV || import.meta.env.VITE_LOCAL_CONVERSATION_RAIL !== '1') return
  try {
    void fetch('/local-chatbot/eventarc/conversation', {
      method: 'POST',
      headers: {
        'ce-id': commandId,
        'ce-type': 'google.cloud.firestore.document.v1.created',
        'ce-subject': `documents/${conversationCommandsPath(projectId)}/${commandId}`,
        'ce-specversion': '1.0',
        'ce-source': 'front-local-rail',
        'content-type': 'application/json',
      },
      body: '{}',
    }).catch(() => undefined)
  } catch {
    /* never in the way of the command */
  }
}

export function sendMessageCommand(
  projectId: string,
  payload: SendMessagePayload,
  options: DepositOptions = {},
): Promise<string> {
  return depositCommand(projectId, 'send_message', payload, options)
}

export function decideActionCommand(
  projectId: string,
  payload: DecideActionPayload,
  options: DepositOptions = {},
): Promise<string> {
  return depositCommand(projectId, 'decide_action', payload, options)
}

export function cancelCommand(
  projectId: string,
  targetCommandId: string,
  options: DepositOptions = {},
): Promise<string> {
  return depositCommand(projectId, 'cancel', { target_command_id: targetCommandId }, options)
}

export function renameCommand(
  projectId: string,
  payload: RenamePayload,
  options: DepositOptions = {},
): Promise<string> {
  return depositCommand(projectId, 'rename', payload, options)
}

// ── MYAC-1269 — the share commands ──────────────────────────────────────────

export function shareCommand(
  projectId: string, payload: SharePayload, options: DepositOptions = {},
): Promise<string> {
  return depositCommand(projectId, 'share', payload, options)
}

export function setRightCommand(
  projectId: string, payload: SetRightPayload, options: DepositOptions = {},
): Promise<string> {
  return depositCommand(projectId, 'set_right', payload, options)
}

export function setVisibilityCommand(
  projectId: string, payload: SetVisibilityPayload, options: DepositOptions = {},
): Promise<string> {
  return depositCommand(projectId, 'set_visibility', payload, options)
}

export function removeParticipantsCommand(
  projectId: string, payload: RemovePayload, options: DepositOptions = {},
): Promise<string> {
  return depositCommand(projectId, 'remove', payload, options)
}

export function leaveConversationCommand(
  projectId: string, chatId: string, options: DepositOptions = {},
): Promise<string> {
  return depositCommand(projectId, 'leave', { chat_id: chatId }, options)
}

export function stopSharingCommand(
  projectId: string, chatId: string, options: DepositOptions = {},
): Promise<string> {
  return depositCommand(projectId, 'stop', { chat_id: chatId }, options)
}

/**
 * Follow one command doc. `cb` receives every observed status; the listener
 * stops by itself on a terminal status. Returns the stop function — the caller
 * keeps it and calls it on teardown.
 */
export function watchCommand(
  projectId: string,
  commandId: string,
  cb: (state: ConversationCommandState) => void,
  options: { firestore?: Firestore; onError?: (e: unknown) => void } = {},
): Unsubscribe {
  const db = options.firestore ?? useFirestore()
  let stopped = false
  let unsubscribe: Unsubscribe = () => {}
  const stop = () => {
    stopped = true
    unsubscribe()
  }
  unsubscribe = onSnapshot(
    doc(db, `${conversationCommandsPath(projectId)}/${commandId}`),
    (snap) => {
      const d = snap.data()
      if (!d || stopped) return
      const status = d.status as ConversationCommandStatus
      cb({
        commandId,
        status,
        reasonKey: (d.reason_key as string) || undefined,
        errorKey: (d.error_key as string) || undefined,
      })
      if (TERMINAL_COMMAND_STATUSES.has(status)) stop()
    },
    (e) => {
      logger.error(`conversation command ${commandId}: listener failed`, e)
      options.onError?.(e)
    },
  )
  // A terminal snapshot delivered synchronously (tests, cache) set `stopped`
  // before `unsubscribe` was the real one.
  if (stopped) unsubscribe()
  return stop
}

/** The sender, as one object — what a component or store picks up. */
export function useConversationCommands() {
  return {
    sendMessageCommand,
    decideActionCommand,
    cancelCommand,
    renameCommand,
    shareCommand,
    setRightCommand,
    setVisibilityCommand,
    removeParticipantsCommand,
    leaveConversationCommand,
    stopSharingCommand,
    watchCommand,
    mintCommandId,
    mintChatId,
    reasonI18nKey: conversationReasonI18nKey,
    errorI18nKey: conversationErrorI18nKey,
  }
}
